/**
 * Page publique de RELECTURE (sans compte) — lien personnel envoyé au relecteur externe
 * (mig 323-324, recette du 28/09). Même présentation que la page de signature M1 :
 * lire le PDF du projet → avis + commentaire → signer avec Signature VPC (tracé ou nom).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SignaturePad } from '@/components/signature/SignaturePad';

type Info = { commune: string; numero: string; titre: string; relecteur: string; expireLe: string;
  avis: { avis: string; commentaire: string | null; le: string } | null };
const AVIS: Array<{ v: 'favorable' | 'reserves' | 'defavorable'; l: string }> = [
  { v: 'favorable', l: 'Favorable' }, { v: 'reserves', l: 'Favorable avec réserves' }, { v: 'defavorable', l: 'Défavorable' },
];
const LIB: Record<string, string> = { favorable: 'favorable', reserves: 'favorable avec réserves', defavorable: 'défavorable' };

export function RelirePage() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<Info | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [lu, setLu] = useState(false);
  const [avis, setAvis] = useState<'favorable' | 'reserves' | 'defavorable' | ''>('');
  const [commentaire, setCommentaire] = useState('');
  const [mode, setMode] = useState<'drawn' | 'typed'>('drawn');
  const [trace, setTrace] = useState<string | null>(null);
  const [nom, setNom] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [fait, setFait] = useState<{ empreinte: string; signeAt: string } | null>(null);

  useEffect(() => {
    fetch(`/api/public/relecture/${encodeURIComponent(token)}`)
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.error ?? 'Lien invalide'); setInfo(j); })
      .catch((e: Error) => setErr(e.message));
  }, [token]);

  async function envoyer() {
    setEnvoi(true); setErr(null);
    try {
      const r = await fetch(`/api/public/relecture/${encodeURIComponent(token)}/avis`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avis, commentaire: commentaire.trim() || null,
          signature: mode === 'drawn' ? { kind: 'drawn', imageDataUrl: trace } : { kind: 'typed', typedNom: nom.trim() } }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? 'Envoi impossible');
      setFait(j);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Envoi impossible'); }
    finally { setEnvoi(false); }
  }
  const peutEnvoyer = lu && !!avis && (mode === 'drawn' ? !!trace : nom.trim().length >= 2) && !envoi;

  return (
    <main id="main-content" className="mx-auto max-w-xl px-4 py-8">
      <h1 className="font-display text-xl font-semibold text-ink">Relecture d’un projet d’arrêté</h1>
      {err && <p className="mt-3 rounded-r-md border border-urgent/40 p-3 text-sm text-urgent" role="alert">{err}</p>}
      {!info && !err && <p className="mt-3 text-sm text-ink-muted">Chargement…</p>}

      {info && (fait || info.avis) && (
        <div className="mt-4 rounded-r-lg border border-success/40 p-4" role="status">
          <p className="font-medium text-ink">Votre avis a été transmis à {info.commune}. Merci.</p>
          {fait
            ? <p className="mt-1 text-xs text-ink-muted">Signé le {new Date(fait.signeAt).toLocaleString('fr-FR')} · empreinte {fait.empreinte}</p>
            : <p className="mt-1 text-xs text-ink-muted">Avis {LIB[info.avis!.avis] ?? info.avis!.avis}, rendu le {new Date(info.avis!.le).toLocaleString('fr-FR')}.</p>}
        </div>
      )}

      {info && !fait && !info.avis && (
        <div className="mt-4 space-y-4">
          <div className="rounded-r-lg border border-border p-4">
            <p className="text-sm text-ink-muted">{info.commune} sollicite votre relecture</p>
            <p className="font-medium text-ink">Arrêté {info.numero} — {info.titre}</p>
            <p className="mt-2 text-sm text-ink-muted">Relecteur : <b className="text-ink">{info.relecteur}</b> · lien valable jusqu’au {new Date(info.expireLe).toLocaleDateString('fr-FR')}</p>
          </div>

          <a className="inline-block text-sm font-medium text-accent underline" target="_blank" rel="noopener noreferrer"
            href={`/api/public/relecture/${encodeURIComponent(token)}/document`} onClick={() => setLu(true)}>
            Lire le projet d’arrêté (PDF)
          </a>
          <label className="flex items-start gap-2 text-sm text-ink">
            <input type="checkbox" className="mt-1" checked={lu} onChange={(e) => setLu(e.target.checked)} />
            J’ai pris connaissance du projet.
          </label>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-ink">Votre avis</legend>
            <div className="flex flex-wrap gap-2 text-sm">
              {AVIS.map((a) => (
                <label key={a.v} className={`cursor-pointer rounded-r-md border px-3 py-1.5 ${avis === a.v ? 'border-accent font-medium' : 'border-border text-ink-muted'}`}>
                  <input type="radio" name="avis" className="sr-only" checked={avis === a.v} onChange={() => setAvis(a.v)} /> {a.l}
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <label htmlFor="commentaire" className="text-sm font-medium text-ink">Vos remarques (facultatif)</label>
            <textarea id="commentaire" rows={4} maxLength={3000} value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
              className="mt-1 w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm" />
          </div>

          <div className="flex gap-2 text-sm" role="radiogroup" aria-label="Manière de signer">
            <button type="button" role="radio" aria-checked={mode === 'drawn'} onClick={() => setMode('drawn')}
              className={`rounded-r-md border px-3 py-1.5 ${mode === 'drawn' ? 'border-accent font-medium' : 'border-border text-ink-muted'}`}>Signer au doigt</button>
            <button type="button" role="radio" aria-checked={mode === 'typed'} onClick={() => setMode('typed')}
              className={`rounded-r-md border px-3 py-1.5 ${mode === 'typed' ? 'border-accent font-medium' : 'border-border text-ink-muted'}`}>Taper mon nom</button>
          </div>
          {mode === 'drawn'
            ? <SignaturePad onChange={setTrace} />
            : <input className="h-11 w-full rounded-r-md border border-border bg-surface px-3" aria-label="Votre nom"
                placeholder="Prénom Nom" value={nom} maxLength={160} onChange={(e) => setNom(e.target.value)} />}

          <button type="button" disabled={!peutEnvoyer} onClick={() => void envoyer()}
            className="h-11 w-full rounded-r-lg bg-accent font-medium text-white disabled:opacity-40">
            {envoi ? 'Envoi…' : 'Signer et transmettre mon avis'}
          </button>
          <p className="text-2xs text-ink-subtle">
            Signature VPC — signature électronique simple (article 1367 du code civil) : la date, l’heure et l’empreinte du
            document sont enregistrées. Vous pouvez aussi répondre directement à l’e-mail reçu.
          </p>
        </div>
      )}
    </main>
  );
}
